//* eslint-disable no-console */
import { deviceType, osName, osVersion, browserVersion, browserName } from "react-device-detect";
import axios from "axios";
import { v4 as uuid } from "uuid";

import { getSessionToken, getStableToken } from "../utils/systemUtils/systemUtils";
import { getBaseHost, getSearchLogsUrl, getSearchLogsToken } from "../utils/url/url";
import { getSystemLang } from "../utils/user-agent";
import { isDevelopment, isProduction } from "../utils/assembly";
import { localStorageSafe, setLocalStorageItem } from "../utils/storage/storage";

const userAgentString = osName + " " + osVersion + " " + browserName + " " + browserVersion;

const isDevelopModule = false;

// Runtime stores
const mapLogTypeToTimeoutId = {};
const currentlySendingLogTypes = new Set();

// Log types
const LOG_TYPE_SEARCH = "logSearch";
const LOG_TYPE_NETWORK = "logNetwork";
const LOG_TYPE_OTHER = "logOther";
export const logTypes = [LOG_TYPE_SEARCH, LOG_TYPE_NETWORK, LOG_TYPE_OTHER];

// AWS logger flags
const isLogNetworkAWS = () => false;
const isLogOtherAWS = () => false;
const isLogSearchAWS = () => isDevelopModule || isProduction();
export const isCanLogAnythingToAWS = () => isLogNetworkAWS() || isLogOtherAWS() || isLogSearchAWS();

// Tech logger flags
const isLogNetworkTech = () => isDevelopment() ? isDevelopModule : false;
const isLogOtherTech = () => isDevelopment() ? isDevelopModule : false;
const isLogSearchTech = () => isDevelopment() ? isDevelopModule : false;
export const isCanLogAnythingToTech = () =>
  isLogNetworkTech() || isLogOtherTech() || isLogSearchTech();

// Common flags
const isLogNetworkNeeded = () => isLogNetworkAWS() || isLogNetworkTech();
const isLogSearchNeeded = () => isLogSearchAWS() || isLogSearchTech();
const isLogOtherNeeded = () => isLogOtherAWS() || isLogOtherTech();
const isCanLogToAWSByLogType = (logType) => {
  if (logType === LOG_TYPE_SEARCH) {
    return isLogSearchAWS();
  }
  return false;
};
export const isCanLogToTechByLogType = (logType) => {
  if (logType === LOG_TYPE_SEARCH) {
    return isLogSearchTech();
  }
  if (logType === LOG_TYPE_OTHER) {
    return isLogOtherTech();
  }
  if (logType === LOG_TYPE_NETWORK) {
    return isLogNetworkTech();
  }
  return false;
};

// Getters
const getSendTimeoutByLogType = (logType) => {
  return 600_000; // 10 minutes
};
const getLogItemsCountMaxByLogType = (logType) => {
  if (logType === LOG_TYPE_OTHER) {
    return 45;
  }
  if (logType === LOG_TYPE_SEARCH) {
    return 30;
  }
  return 60;
};
const techLogsEndpointOrigin = isDevelopModule ? "http://localhost:8080" : "https://yo.pages.egwhite.net";
export const makeTechLogsEndpoint = (logType) => {
  let endPoint;
  if (logType === LOG_TYPE_NETWORK) {
    endPoint = "network";
  } else if (logType === LOG_TYPE_OTHER) {
    endPoint = "other";
  } else if (logType === LOG_TYPE_SEARCH) {
    endPoint = "search";
  }
  return techLogsEndpointOrigin + "/api/v1/log/" + endPoint;
};

// Other
export const OtherLogEvents = {
  READ_CHAPTER: "read_chapter",
  LISTEN_CHAPTER: "listen_chapter",

  CLICK_SIDE_MENU: "click_side_menu",
  TREE_ITEM_CLICK: "tree_item_click",
  BREADCRUMBS_ITEM_CLICK: "breadcrumbs_item_click",

  READER_ACTION: "reader_action",
  BOOK_TOC_ACTION: "book_toc_action",

  BUY_PRINTED: "buy_printed",
  DOWNLOAD: "download",

  SHARE: "share",
  COPY: "copy",
  PRINT: "print",

  BUY_PACKAGE: "buy_package",
  BUY_BOOK: "buy_book",

  LOGIN: "login",
  BOOK_INTERACTION: "book_interaction",
};

export const logOtherEvent = (eventType, eventData) => {
  if (!isLogOtherNeeded()) {
    return;
  }
  // TODO add some data
  const webpage_url = window.location.origin + window.location.pathname;
  const fullEventData = {
    event_id: uuid(),
    event_type: eventType,
    params: eventData,
    user_id: window.userId || "",
    user_lang: window.userLang || getSystemLang(),
    date: new Date().toISOString(),
    cookie_id: getStableToken(),
    session_id: getSessionToken(),
    webpage_url,
    device_type: deviceType,
  };
  logEvent(LOG_TYPE_OTHER, fullEventData);
};

// Search
export const SearchLogEvents = {
  SEARCH: "search",
  SEARCH_SHORTCUT: "search_shortcut",
  SEARCH_CHANGE_LANG: "search_change_lang",
  SEARCH_LOAD_MORE: "search_load_more",
  SEARCH_CHANGE_POSITION: "search_change_position",
  SEARCH_CLICK: "search_click",
  SEARCH_CLICK_CORRECTION: "search_click_correction",
  SEARCH_CLICK_SUGGESTION: "search_click_suggestion",
  SEARCH_CLICK_HISTORY: "search_click_history",

  PAGE_VIEW: "pageview",
  LOGIN: "login",
};
const SearchLogEventsUnnecessaryForTech = [
  SearchLogEvents.PAGE_VIEW,
  SearchLogEvents.LOGIN,
];

export const logSearchEvent = (eventType, eventData) => {
  if (!isLogSearchNeeded()) {
    return;
  }
  // TODO add some data
  const webpage_url = window.location.origin + window.location.pathname;
  const fullEventData = {
    event_id: uuid(),
    event_type: eventType,
    params: eventData,
    user_id: window.userId || "",
    user_lang: window.userLang || getSystemLang(),
    date: new Date().toISOString(),
    cookie_id: getStableToken(),
    session_id: getSessionToken(),
    webpage_url,
    device_type: deviceType,
  };
  logEvent(LOG_TYPE_SEARCH, fullEventData);
};

// Network
export const logNetworkEvent = (eventData) => {
  if (!isLogNetworkNeeded()) {
    return;
  }
  // TODO add some data
  const fullEventData = {
    ...eventData,
    userId: window.userId || "",
    ua: userAgentString,
    appUrl: getBaseHost(),
    date: new Date().toISOString(),
  };
  logEvent(LOG_TYPE_NETWORK, fullEventData);
};

// Common
export const getStoredLogs = (logType) => {
  const defaultValue = [];
  const analiticDataString = localStorageSafe.getItem(logType);
  let analiticDataJson;
  if (analiticDataString) {
    try {
      analiticDataJson = JSON.parse(analiticDataString);
      if (!Array.isArray(analiticDataJson)) {
        analiticDataJson = defaultValue;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log("background: black; color: white;[getStoredLogs] Error: ", analiticDataString);
    }
  } else {
    analiticDataJson = defaultValue;
  }
  return analiticDataJson;
};

const logEvent = (logType, eventData) => {
  clearTimeout(mapLogTypeToTimeoutId[logType]);
  const analiticDataJson = getStoredLogs(logType);
  analiticDataJson.push(eventData);
  setLocalStorageItem(logType, JSON.stringify(analiticDataJson));

  if (isDevelopModule) {
    console.log(`%c${analiticDataJson.length}`,
      `background: ${logType === LOG_TYPE_NETWORK ? "red" 
        : logType === LOG_TYPE_SEARCH ? "blue" : "green"}; color: white;`
    );
  }

  const isSendImmediately = analiticDataJson.length > getLogItemsCountMaxByLogType(logType)
    && !currentlySendingLogTypes.has(logType);

  if (isSendImmediately) {
    if (isDevelopModule) {
      console.log(`%cSend immediately ${logType}`, "background: gold; color: white;");
    }
    sendLogsByType(logType);
  } else {
    mapLogTypeToTimeoutId[logType] = setTimeout(() => {
      sendLogsByType(logType);
    }, getSendTimeoutByLogType(logType));
  }
};

export const flushStoredLogsByData = (logType, logData) => {
  const allData = getStoredLogs(logType);
  const simpleList = logData.map((item) => {
    return item.date;
  });
  const clearList = allData.filter((item) => !simpleList.includes(item.date));
  if (isDevelopModule) {
    console.log(`flushLogData, new list: `, logType, clearList);
  }
  setLocalStorageItem(logType, JSON.stringify(clearList));
};

const prepareLogDataForTech = (logType, data) => {
  if (logType === LOG_TYPE_SEARCH) {
    return data.filter((item) => !SearchLogEventsUnnecessaryForTech.includes(item.event_type));
  }
  return data;
};

const prepareLogDataForAWS = (logType, data) => {
  let records = [];
  if (logType === LOG_TYPE_SEARCH) {
    records = data.map((item) => {
      const out = {...item};

      out.language = item.user_lang;
      out.timestamp = item.date;
      out["event_data"] = item.params;

      delete out.user_lang;
      delete out.date;
      delete out.params;

      return out;
    });
  }

  return {records};
};

const sendLogsByType = async (logType) => {
  const logData = getStoredLogs(logType);
  if (isDevelopModule) {
    console.groupCollapsed("Send logs group");
    console.log({ logType, logData });
  }
  if (logData.length) {
    let egwTechReqResponse, awsReqResponse, isEgwTechError, isAWSError;
    const isCanSendToEgwTechLogger = isCanLogToTechByLogType(logType);
    const isCanSendToAWSLogger = isCanLogToAWSByLogType(logType);

    currentlySendingLogTypes.add(logType);

    // EGW Tech logger
    if (isCanSendToEgwTechLogger) {
      try {
        egwTechReqResponse = await axios({
          method: "post",
          url: makeTechLogsEndpoint(logType),
          data: prepareLogDataForTech(logType, logData),
        });
      } catch (e) {
        isEgwTechError = true;
      }
    }

    // AWS logger
    if (isCanSendToAWSLogger) {
      try {
        awsReqResponse = await axios({
          method: "post",
          headers: {
            "x-api-key": getSearchLogsToken(),
          },
          url: getSearchLogsUrl(),
          data: prepareLogDataForAWS(logType, logData),
        });
      } catch (e) {
        isAWSError = true;
      }
    }

    // for now, ignore if some api failed to not overfill the storage or send the same data twice.
    const isCanFLush = (isCanSendToAWSLogger && !isAWSError)
      || (isCanSendToEgwTechLogger && !isEgwTechError);

    if (isCanFLush) {
      flushStoredLogsByData(logType, logData);
    }

    currentlySendingLogTypes.delete(logType);

    if (isDevelopModule) {
      console.log("Flush " + (isCanFLush ? "✅" : "❌"));
      console.table({
        isCanSendToAWSLogger,
        isCanSendToEgwTechLogger,
        isAWSError,
        isEgwTechError,
      });
      console.log("Logs requests: ", { egwTechReqResponse, awsReqResponse, });
    }
  }
  if (isDevelopModule) {
    console.groupEnd("Send logs method");
  }
};
